import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
// Rxjs
import { Observable , of} from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
// Environment
import { environment } from 'src/environments/environment';
// Model
import { MessageType } from '../../components/messages/message-handler/message-handler.component';
import { MessageHandlerService } from '../../components/messages/message-handler/message-handler.service';
import { SaveType } from '../../core/models/enumerations/save-type';
// Interface
import { TranslateService } from '@ngx-translate/core';
import { LoadWheelService } from '../../components/load-wheel/load-wheel.service';

import { UnitLTSASearch, UnitLTSASearchAdapter } from 'src/app/core/models/unit-ltsa-search';
import { UnitsLTSADisplay } from 'src/app/core/models/unit-ltsa/display/units-ltsa-display';
import { EntityName } from '../../core/models/enumerations/entity-name';
import { SpinnerProcess } from '../../core/models/spinner-process';
import { RecordLockingFlow } from '../../core/record-locking/record-locking-flow';
import { EntityCommonService } from '../../core/services/entity-common.service';
import { LockSameUserMessageService, LockSameUserResult } from '../../core/services/lock-same-user-message.service';
import { TelemetryService } from '../../core/services/telemetry.service';
import { EntityUtilsService } from '../../core/utils/entity-utils.service';

@Injectable({
    providedIn: 'root'
})
export class UnitLtsaSectionService {

    constructor(
        private readonly adapter: UnitLTSASearchAdapter,
        private readonly recordLockingFlow: RecordLockingFlow,
        private readonly http: HttpClient,
        private readonly messageHandlerService: MessageHandlerService,
        private readonly loadWheelService: LoadWheelService,
        private readonly entityCommonService: EntityCommonService,
        private readonly translate: TranslateService,
        private readonly unitLTSASearchAdapter: UnitLTSASearchAdapter,
        private readonly entityUtilsService: EntityUtilsService,
        private readonly lockSameUserMessageService: LockSameUserMessageService,
        private readonly telemetry: TelemetryService,
    ) {
    }

    getUnitLtsaDetailsById(ltsaId: string, isReload: boolean = false, quickAccess: boolean = false): Promise<UnitsLTSADisplay> {
        let mainLockID: number = 0;

        if (isReload) {
            mainLockID = this.recordLockingFlow.getLockID(EntityName.UNIT_LTSA, ltsaId);
        }

        return new Promise<UnitsLTSADisplay>((resolve) => {
            this.lockSameUserMessageService.validateEntityIsLockedByUser(this.recordLockingFlow, EntityName.UNIT_LTSA, ltsaId, mainLockID, quickAccess).then((lockSameUserResult: LockSameUserResult) => {
                if(lockSameUserResult.openEntity) {
                    this.getEntityDetailsById(ltsaId, mainLockID, false).subscribe((entityDisplay: UnitsLTSADisplay) => {
                        resolve(entityDisplay);
                    });
                } else {
                    resolve(UnitsLTSADisplay.BuildUnitLTSADisplay({}));
                }
            });
        });
    }

    private getEntityDetailsById(ltsaId: string, mainLockID: number, onlyForRead: boolean): Observable<UnitsLTSADisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.unitLTSA") + ' (' + this.translate.instant("loading.mainRecord") + ')');

        return this.http.get<UnitsLTSADisplay>(`${environment.apiUrl}unitLtsa/${ltsaId}/${mainLockID}/${onlyForRead}`)
            .pipe(
                map((data: any) => {
                    let ltsaDisplay = UnitsLTSADisplay.BuildUnitLTSADisplay({});
                    if (data.response) {
                        ltsaDisplay = UnitsLTSADisplay.BuildUnitLTSADisplay(data.response);

                        if (ltsaDisplay.unitLtsaId) {
                            if (ltsaDisplay.hasDiffUserLockError()) {
                                this.messageHandlerService.show(ltsaDisplay.lockError, MessageType.INFO);
                            }

                            // Set the locking info into lock global list.
                            this.recordLockingFlow.setLockItem(EntityName.UNIT_LTSA, ltsaDisplay.unitLtsaId, ltsaDisplay.lockId, ltsaDisplay.lockMode);
                            this.recordLockingFlow.initLockRefreshTimer(EntityName.UNIT_LTSA, ltsaDisplay.unitLtsaId, ltsaDisplay.lockId);
                        } else {
                            this.messageHandlerService.show(ltsaDisplay.lockError, MessageType.ERROR);
                        }
                    } else {
                        const errorType = MessageHandlerService.errorType(data);
                        if (errorType === MessageType.INFO) {
                            this.entityCommonService.sendEntityNotFoundEvent();
                        } else if (errorType !== MessageType.SESSION_INVALID) {
                            this.messageHandlerService.show(MessageHandlerService.errorMessage(data), errorType);
                        }
                    }
                    return ltsaDisplay;
                }),
                catchError((error) => {
                    return of(UnitsLTSADisplay.BuildUnitLTSADisplay({}));
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    getLtsaById(ltsaId: number): Observable<UnitLTSASearch> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.unitLTSA") + ' (' + this.translate.instant("loading.mainRecord") + ')');
        return this.http.get<UnitLTSASearch>(`${environment.apiUrl}unitLtsa/${ltsaId}`)
            .pipe(
                map((data: any) => {
                        return this.adapter.adapt(data.response[0]);
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    saveLtsaDetails(unitsLTSADisplay: UnitsLTSADisplay): Observable<UnitsLTSADisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("saving.ltsa"));
        return this.http.put<UnitsLTSADisplay>(`${environment.apiUrl}unitLtsa`, unitsLTSADisplay)
            .pipe(
                map((data: any) => {
                    if (data.response === null) {
                        if (data.exception !== null) {
                            this.messageHandlerService.showExceptionDialog(data);
                        } else if (data.message !== null) {
                            if (unitsLTSADisplay.saveType === SaveType.TYPE_QC || unitsLTSADisplay.saveType === SaveType.UPDATE) {
                                //When the user attempts to QC a record while the lock was already cleared,
                                //this//the above warning message should still be thrown; however, the record should not reload.
                                if (data.messageType !== MessageType.WARNING.valueOf()) {
                                    this.entityCommonService.sendReloadEvent();
                                }
                            } else {
                                unitsLTSADisplay.intDataDepMigError = data.message;
                            }

                        }
                    } else {
                        const isQcUpdateOrAmend = unitsLTSADisplay.saveType === SaveType.TYPE_QC ||
                            unitsLTSADisplay.saveType === SaveType.AMENDMENT ||
                            unitsLTSADisplay.saveType === SaveType.UPDATE;

                        const afterClearFunc = () => this.entityCommonService.sendPreReleaseEvent();
                        this.recordLockingFlow.clearLockFlow(EntityName.UNIT_LTSA, String(unitsLTSADisplay.unitLtsaId), isQcUpdateOrAmend, afterClearFunc);
                    }
                    return data.response;
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    ltsaNextId(): Observable<UnitsLTSADisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.nextId"));
        return this.http.get<any>(`${environment.apiUrl}unitLtsa/nextId`)
            .pipe(
                map((data: any) => {
                    const unitsLTSADisplay = UnitsLTSADisplay.BuildNewUnitLTSA(data.response);
                    if (unitsLTSADisplay?.unitLtsaId) {
                        // Set the locking info into lock global list.
                        this.recordLockingFlow.setLockItem(EntityName.UNIT_LTSA, unitsLTSADisplay.unitLtsaId, -1);
                    }
                    return unitsLTSADisplay;
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    searchUnitLTSAs(httpParams: HttpParams): Observable<UnitLTSASearch[]> {
        const searchStart: number = performance.now();
       
        return this.http.get<UnitLTSASearch[]>(`${environment.apiUrl}unitLtsa/searchUnitLTSAs`, { params: httpParams })
            .pipe(
                map((data: any) => {
                    this.entityUtilsService.verifyMaxResult(data);
                    let ltsaList: Array<any> = data.response;

                    if (ltsaList.length > 1) {
                        ltsaList = this.removeDuplicatedLtsas(ltsaList);
                        ltsaList = this.sortLtsaById(ltsaList);
                    }
                    
                    this.telemetry.entitySearchTime("unit LTSA", performance.now() - searchStart);

                    return ltsaList.map(item => this.unitLTSASearchAdapter.adapt(item));
                })
            )
    }

    private removeDuplicatedLtsas(ltsas: Array<any>): Array<any> {
        let uniqueLtsas: Array<any> = [];
        ltsas.forEach(ltsa => {
            if (!uniqueLtsas.some(uniqueLtsa => uniqueLtsa.UNIT_LTSA_ID == ltsa.UNIT_LTSA_ID)) {
                uniqueLtsas.push(ltsa);
            }
        });
        return uniqueLtsas;
    }

    private sortLtsaById(ltsas: Array<any>): Array<any> {
        return ltsas.sort((a, b) => {
            if (a.UNIT_LTSA_ID < b.UNIT_LTSA_ID) return -1;
            if (a.UNIT_LTSA_ID > b.UNIT_LTSA_ID) return 1;
            return 0;
        });
    }

}
